import { useStore } from "@nanostores/preact";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import type { MailThread } from "../net/api";
import {
  $mailDrafts, $mailFailed, $mailInterval, $mailSelected, $mailSummary, $mailThread, checkedText, loadMailSummary, loadThread,
  selectAgent, sendMail, setDraft, setMailInterval, timeText, type MailInterval,
} from "../state/mail";
import { $connection, $panel, toast } from "../state/stores";
import { AgentPromptButton } from "./AgentPromptButton";
import { describeError } from "./errors";
import { MAIL_INTERVALS, mailPrompt } from "./mailPrompt";
import { Panel } from "./Panel";

const MAX_RUNES = 2000;
const runes = (s: string) => [...s].length;

/** M: write to your agents, read their replies, copy the schedule prompt (spec 2j §2.2). */
export function MailPanel() {
  const summary = useStore($mailSummary);
  const failed = useStore($mailFailed);
  const selected = useStore($mailSelected);
  useEffect(() => {
    // every opening starts on the first agent and re-reads its thread, so mail that came while the
    // panel was closed is shown and marked read (spec 2j §2.2, §2.3)
    $mailSelected.set(null);
    $mailThread.set(null);
    void loadMailSummary();
  }, []);
  useEffect(() => {
    const threads = summary?.threads ?? [];
    if (threads.length > 0 && !threads.some((t) => t.agent.id === selected)) selectAgent(threads[0]!.agent.id);
  }, [summary, selected]);
  return (
    <Panel title="信箱" wide onClose={() => $panel.set(null)}>
      <PromptBar />
      {failed && (
        <div class="error">信箱暫時無法載入 <button class="secondary" onClick={() => void loadMailSummary()}>重試</button></div>
      )}
      {summary === null ? (!failed && <p class="hint">載入中…</p>)
        : summary.threads.length === 0 ? (
          <>
            <p>你還沒有 agent。</p>
            <AgentPromptButton />
          </>
        ) : (
          <div class="mail">
            <AgentList threads={summary.threads} selected={selected} />
            {selected && <ThreadView agentId={selected} />}
          </div>
        )}
    </Panel>
  );
}

function PromptBar() {
  const interval = useStore($mailInterval);
  const [manual, setManual] = useState<string | null>(null);
  async function copy() {
    const text = mailPrompt(interval);
    try {
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(text);
      setManual(null);
      toast("已複製排程提示詞");
    } catch {
      setManual(text);
    }
  }
  return (
    <div class="mail-prompt">
      <label>
        查信間隔
        <select value={interval} onChange={(e) => setMailInterval((e.currentTarget as HTMLSelectElement).value as MailInterval)}>
          {MAIL_INTERVALS.map(([v, words]) => <option key={v} value={v}>{`每 ${words}`}</option>)}
        </select>
      </label>
      <button class="secondary" onClick={() => void copy()}>複製排程提示詞</button>
      {manual && (
        <div class="prompt-manual">
          <p class="hint">請按 ⌘C／Ctrl+C 複製</p>
          <textarea readOnly rows={8} value={manual} ref={(el) => el?.select()} />
        </div>
      )}
    </div>
  );
}

function AgentList(props: { threads: MailThread[]; selected: string | null }) {
  const now = Date.now();
  return (
    <ul class="mail-agents">
      {props.threads.map((t) => (
        <li key={t.agent.id} class={t.agent.id === props.selected ? "active" : ""}>
          <button type="button" onClick={() => selectAgent(t.agent.id)}>
            <div class="mail-agent-name">
              <span>{t.agent.display_name}</span>
              {t.unread > 0 && <span class="mail-unread">{t.unread}</span>}
            </div>
            {t.last && <div class="mail-preview">{t.last.from_agent ? "" : "你："}{t.last.preview}</div>}
            <div class="mail-checked">{checkedText(t.checked_at, now)}</div>
          </button>
        </li>
      ))}
    </ul>
  );
}

function ThreadView(props: { agentId: string }) {
  const thread = useStore($mailThread);
  const drafts = useStore($mailDrafts);
  const conn = useStore($connection);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const mine = thread?.agentId === props.agentId ? thread : null;
  const draft = drafts[props.agentId] ?? "";
  const n = runes(draft);
  useEffect(() => { setError(null); atBottom.current = true; }, [props.agentId]);
  // follow new messages only when the reader was already at the bottom (as chat does)
  useLayoutEffect(() => {
    const el = list.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, [mine?.messages.length]);

  async function submit() {
    // read the store, not this render's draft: Enter can arrive before useStore's re-render timer
    const text = $mailDrafts.get()[props.agentId] ?? "";
    if (busy || !text.trim() || runes(text) > MAX_RUNES) return;
    if (conn !== "online") { setError("離線中，信沒寄出"); return; }
    setBusy(true);
    setError(null);
    atBottom.current = true;
    const code = await sendMail(props.agentId, text);
    setBusy(false);
    if (code && code !== "AGENT_NOT_FOUND") setError(describeError(code, "信沒寄出，請再試一次"));
  }
  function onKeyDown(e: KeyboardEvent) {
    if (e.key !== "Enter" || e.shiftKey) return;
    if (e.isComposing || e.keyCode === 229) return; // an input method is still composing (Review Focus 1)
    e.preventDefault();
    void submit();
  }
  const now = Date.now();
  return (
    <div class="mail-thread">
      <div class="mail-messages" ref={list}
        onScroll={(e) => { const el = e.currentTarget as HTMLDivElement; atBottom.current = el.scrollTop + el.clientHeight >= el.scrollHeight - 8; }}>
        {!mine && <p class="hint">載入中…</p>}
        {mine?.hasMore && (
          <button class="secondary" onClick={() => void loadThread(props.agentId, mine.messages[0]!.id)}>載入更早的信</button>
        )}
        {mine && mine.messages.length === 0 && <p class="hint">還沒有信。寫點什麼給這個 agent 吧。</p>}
        {mine?.messages.map((m) => (
          <div key={m.id} class={m.from_agent ? "mail-msg agent" : "mail-msg mine"}>
            <div class="mail-body">{m.body}</div>
            <div class="mail-meta">{timeText(m.created_at, now)}{!m.from_agent && (m.read_at ? " · 已讀" : " · 未讀")}</div>
          </div>
        ))}
      </div>
      {error && <div class="error">{error}</div>}
      <textarea rows={3} value={draft} readOnly={busy} placeholder="寫信給這個 agent（Enter 送出，Shift+Enter 換行）"
        onInput={(e) => setDraft(props.agentId, (e.currentTarget as HTMLTextAreaElement).value)} onKeyDown={onKeyDown} />
      <div class="mail-compose">
        <span class={n > MAX_RUNES ? "mail-count over" : "mail-count"}>{n} / {MAX_RUNES}</span>
        <button disabled={busy || !draft.trim() || n > MAX_RUNES} onClick={() => void submit()}>送出</button>
      </div>
    </div>
  );
}
